/**
 * Time and attendance.
 *
 * Two screens in one file, because they are two ends of the same fact
 * and share every type between them.
 *
 * ---------------------------------------------------------------------
 * MY TIME — one button, and it is the right one
 *
 * A person opening this on their phone at ten to four wants to tap in.
 * So the page is a single large button that says either "Clock in" or
 * "Clock out", and everything else — the shift they are due on, the
 * hours so far this week — sits underneath it as context, not as a
 * choice. A time clock that makes somebody pick from a menu while a
 * queue forms at the bar is a time clock people work around.
 *
 * ---------------------------------------------------------------------
 * THE TIMESHEET — the variance is the column
 *
 * Worked, rostered, and the difference. The difference is the reason
 * this module exists, so it is not the last column: a manager scanning
 * eighty rows is looking for the ones that are not near zero.
 *
 * A blank variance is deliberate and different from zero. Nobody
 * rostered that hour at all, and showing it as "0.00" would say the
 * opposite of what happened.
 *
 * ---------------------------------------------------------------------
 * THE FORGOTTEN CLOCK-OUTS COME FIRST
 *
 * Above the range, above the totals, and NOT filtered by the week being
 * looked at — an entry left open two weeks ago is exactly the one
 * somebody needs to be shown, and it is not in the week they opened.
 * Every hour in the totals below is wrong until those are fixed.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Entry {
  id: string;
  staff_member_id: string;
  display_name: string;
  job_title: string;
  department: string;
  department_id: string;
  work_date: string;
  started_at: string;
  ended_at: string | null;
  ends_next_day: boolean;
  break_minutes: number;
  source: string;
  status: string;
  worked_hours: string;
  scheduled_minutes: number | null;
  variance_minutes: number | null;
  unscheduled: boolean;
  notes: string | null;
}

interface Week {
  week_starting: string;
  staff_member_id: string;
  display_name: string;
  department: string;
  department_id: string;
  is_exempt: boolean;
  entries: string;
  still_open: string;
  awaiting_approval: string;
  unscheduled_entries: string;
  worked_hours: string;
  scheduled_hours: string | null;
  variance_hours: string;
  overtime_hours: string;
}

function clock(t: string | null): string {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = (h as number) < 12 ? 'am' : 'pm';
  const hour = (h as number) % 12 === 0 ? 12 : (h as number) % 12;
  return m === 0 ? `${hour}${ampm}` : `${hour}:${String(m).padStart(2, '0')}${ampm}`;
}

/** −1.75 → "1h 45m under". A signed decimal is not what anybody reads. */
function variance(minutes: number | null): { text: string; tone: 'good' | 'warning' | 'neutral' } {
  if (minutes === null) return { text: 'not rostered', tone: 'neutral' };
  if (minutes === 0) return { text: 'to the minute', tone: 'good' };
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const size = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return {
    text: `${size} ${minutes > 0 ? 'over' : 'under'}`,
    tone: abs >= 60 ? 'warning' : 'good',
  };
}

function mondayOf(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// =====================================================================
// My time
// =====================================================================

export function MyTimePage() {
  const { data, error, loading, reload } = useApi<{
    employments: any[];
    open: Entry | null;
    today: any[];
    week: Week[];
  }>('/v1/time/me');

  const [breakMinutes, setBreakMinutes] = useState('30');

  const clockIn = useMutation(async (staffId: string) =>
    api('/v1/time/clock-in', { method: 'POST', body: { staff_member_id: staffId } }),
  );
  const clockOut = useMutation(async (entryId: string) =>
    api('/v1/time/clock-out', {
      method: 'POST',
      body: { entry_id: entryId, break_minutes: Number(breakMinutes) || 0 },
    }),
  );

  if (loading && !data) return <Loading />;

  const employments = data?.employments ?? [];
  const open = data?.open ?? null;
  const thisWeek = (data?.week ?? [])[0];

  if (employments.length === 0) {
    return (
      <>
        <h1>My Time</h1>
        <Card title="Nothing to Clock">
          <Empty>
            You have a login here but no employment on record, so there is nothing to clock in
            against. If that is wrong, whoever keeps the staff records can add one.
          </Empty>
        </Card>
      </>
    );
  }

  return (
    <>
      <h1>My Time</h1>
      <ErrorBanner error={error ?? clockIn.error ?? clockOut.error} />

      <Card title={open ? 'You are clocked in' : 'You are not clocked in'}>
        {open ? (
          <>
            <p>
              Since {clock(open.started_at)} on {fmtDate(open.work_date)}
              {open.scheduled_minutes !== null && (
                <span className="muted"> · against a rostered shift</span>
              )}
              {open.unscheduled && (
                <span className="muted"> · nobody rostered this, which is fine</span>
              )}
            </p>
            <label className="field">
              <span>Break taken, in minutes</span>
              <input
                type="number"
                min={0}
                max={720}
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(e.target.value)}
              />
              <div className="hint">Unpaid time. Leave it at zero if you did not take one.</div>
            </label>
            <div className="actions">
              <button
                disabled={clockOut.busy}
                onClick={async () => {
                  const r = await clockOut.run(open.id);
                  if (r) reload();
                }}
              >
                {clockOut.busy ? 'Clocking out…' : 'Clock out'}
              </button>
            </div>
          </>
        ) : (
          <div className="actions">
            {employments.map((e) => (
              <button
                key={e.id}
                disabled={clockIn.busy}
                onClick={async () => {
                  const r = await clockIn.run(e.id);
                  if (r) reload();
                }}
              >
                {clockIn.busy
                  ? 'Clocking in…'
                  : employments.length === 1
                    ? 'Clock in'
                    : `Clock in — ${e.job_title}, ${e.department}`}
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card title="What You Are Due to Work">
        {(data?.today ?? []).length === 0 ? (
          <Empty>Nothing on the schedule for you around today.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Job</th>
                  <th>Hours</th>
                  <th>Where</th>
                </tr>
              </thead>
              <tbody>
                {(data?.today ?? []).map((s: any) => (
                  <tr key={s.id}>
                    <td data-label="Day" className="cell--title">
                      {fmtDate(s.work_date)}
                    </td>
                    <td data-label="Job">{s.job_title}</td>
                    <td data-label="Hours">
                      {clock(s.starts_at)}–{clock(s.ends_at)}
                      {s.status === 'cancelled' && (
                        <span className="muted small"> · cancelled: {s.cancelled_reason}</span>
                      )}
                    </td>
                    <td data-label="Where" className="muted cell--aside">
                      {s.department}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {thisWeek && (
        <div className="tiles">
          <Tile label="This week" value={`${thisWeek.worked_hours}h`} />
          <Tile
            label="Rostered"
            value={thisWeek.scheduled_hours ? `${thisWeek.scheduled_hours}h` : '—'}
          />
          <Tile
            label="Overtime"
            value={`${thisWeek.overtime_hours}h`}
            note={thisWeek.is_exempt ? 'salaried — not tracked' : undefined}
          />
          <Tile
            label="Awaiting approval"
            value={String(thisWeek.awaiting_approval)}
            note={Number(thisWeek.awaiting_approval) > 0 ? 'your manager signs these off' : 'all signed off'}
          />
        </div>
      )}
    </>
  );
}

// =====================================================================
// The timesheet
// =====================================================================

export function TimesheetsPage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [department, setDepartment] = useState('');
  const weekEnd = addDays(weekStart, 6);

  const params = new URLSearchParams({ from: weekStart, to: weekEnd });
  if (department) params.set('department', department);

  const { data, error, loading, reload } = useApi<{
    entries: Entry[];
    weeks: Week[];
    forgotten: any[];
    may_reopen: boolean;
  }>(`/v1/time?${params}`, [weekStart, department]);

  const approve = useMutation(async () =>
    api('/v1/time/approve', { method: 'POST', body: { from: weekStart, to: weekEnd } }),
  );

  if (loading && !data) return <Loading />;

  const entries = data?.entries ?? [];
  const weeks = data?.weeks ?? [];
  const forgotten = data?.forgotten ?? [];

  const departments = Array.from(
    new Map(entries.map((e) => [e.department_id, e.department])).entries(),
  );

  const awaiting = entries.filter((e) => e.status === 'recorded').length;
  const worked = entries.reduce((n, e) => n + Number(e.worked_hours), 0);
  const rostered = entries.reduce((n, e) => n + (e.scheduled_minutes ?? 0), 0) / 60;
  const overtime = weeks.reduce((n, w) => n + Number(w.overtime_hours), 0);

  return (
    <>
      <h1>Timesheets</h1>
      <ErrorBanner error={error ?? approve.error} />

      {/* First, and unfiltered: every hour below is wrong until these
          are fixed, and the one from two weeks ago is not in the week
          anybody has open. */}
      {forgotten.length > 0 && (
        <Card title={`${forgotten.length} still clocked in`}>
          <p className="muted small">
            These entries were never closed. Until they are, the hours below are wrong — and
            nothing here will guess a finish time and pay it.
          </p>
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Who</th>
                  <th>Since</th>
                  <th>Open for</th>
                  <th>Should have ended</th>
                </tr>
              </thead>
              <tbody>
                {forgotten.map((f: any) => (
                  <tr key={f.id}>
                    <td data-label="Who" className="cell--title">
                      {f.display_name}
                      <span className="muted small cell--note"> · {f.department}</span>
                    </td>
                    <td data-label="Since">
                      {fmtDate(f.work_date)} at {clock(f.started_at)}
                    </td>
                    <td data-label="Open for">
                      <Status tone={f.hours_open > 24 ? 'serious' : 'warning'}>
                        {f.hours_open} hours
                      </Status>
                    </td>
                    <td data-label="Should have ended" className="muted cell--aside">
                      {f.should_have_ended_at ? clock(f.should_have_ended_at) : 'no shift behind it'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card
        title={`Week of ${fmtDate(weekStart)}`}
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="secondary small" onClick={() => setWeekStart(addDays(weekStart, -7))}>
              ← Previous
            </button>
            <button className="secondary small" onClick={() => setWeekStart(mondayOf(new Date()))}>
              This week
            </button>
            <button className="secondary small" onClick={() => setWeekStart(addDays(weekStart, 7))}>
              Next →
            </button>
            {departments.length > 1 && (
              <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">Every department</option>
                {departments.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </div>
        }
      >
        <div className="tiles">
          <Tile label="Hours worked" value={worked.toFixed(1)} />
          <Tile label="Hours rostered" value={rostered > 0 ? rostered.toFixed(1) : '—'} />
          <Tile
            label="Difference"
            value={`${worked - rostered >= 0 ? '+' : ''}${(worked - rostered).toFixed(1)}`}
            note={rostered > 0 ? `${(((worked - rostered) / rostered) * 100).toFixed(1)}%` : undefined}
            tone={Math.abs(worked - rostered) > rostered * 0.05 ? 'warning' : 'good'}
          />
          <Tile
            label="Overtime"
            value={overtime.toFixed(1)}
            note="past forty hours, non-exempt only"
            tone={overtime > 0 ? 'warning' : 'good'}
          />
        </div>

        <div className="actions">
          <button
            disabled={approve.busy || awaiting === 0}
            onClick={async () => {
              const r = await approve.run();
              if (r) reload();
            }}
          >
            {approve.busy
              ? 'Approving…'
              : awaiting === 0
                ? 'Nothing awaiting approval'
                : `Approve ${awaiting} entr${awaiting === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
        {entries.some((e) => e.status === 'open') && (
          <p className="muted small">
            Entries still open are left alone — approving one would invent a finish time and then
            pay it.
          </p>
        )}
      </Card>

      <Card title="By Person">
        {weeks.length === 0 ? (
          <Empty>Nobody worked in this week.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Who</th>
                  <th className="num">Worked</th>
                  <th className="num">Rostered</th>
                  <th className="num">Difference</th>
                  <th className="num">Overtime</th>
                  <th>Department</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => (
                  <tr key={`${w.week_starting}-${w.staff_member_id}`}>
                    <td data-label="Who" className="cell--title">
                      {w.display_name}
                      {Number(w.unscheduled_entries) > 0 && (
                        <span className="muted small cell--note">
                          {' '}
                          · {w.unscheduled_entries} not rostered
                        </span>
                      )}
                    </td>
                    <td data-label="Worked" className="num">
                      {w.worked_hours}
                    </td>
                    <td data-label="Rostered" className="num">
                      {w.scheduled_hours ?? '—'}
                    </td>
                    <td data-label="Difference" className="num">
                      {Number(w.variance_hours) > 0 ? '+' : ''}
                      {w.variance_hours}
                    </td>
                    <td data-label="Overtime" className="num">
                      {Number(w.overtime_hours) > 0 ? (
                        <Status tone="warning">{w.overtime_hours}</Status>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-label="Department" className="muted cell--aside">
                      {w.department}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Every Entry">
        {entries.length === 0 ? (
          <Empty>No hours recorded in this week.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Who</th>
                  <th>In and out</th>
                  <th className="num">Worked</th>
                  <th>Against the roster</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const v = variance(e.variance_minutes);
                  return (
                    <tr key={e.id}>
                      <td data-label="Day">{fmtDate(e.work_date)}</td>
                      <td data-label="Who" className="cell--title">
                        {e.display_name}
                        <span className="muted small cell--note"> · {e.job_title}</span>
                      </td>
                      <td data-label="In and out">
                        {clock(e.started_at)}–{clock(e.ended_at)}
                        {e.ends_next_day && <span className="muted small"> next day</span>}
                        {e.break_minutes > 0 && (
                          <span className="muted small"> · {e.break_minutes}m break</span>
                        )}
                      </td>
                      <td data-label="Worked" className="num">
                        {e.worked_hours}
                      </td>
                      <td data-label="Against the roster">
                        {/* A blank is not a zero: nobody rostered it. */}
                        <Status tone={v.tone}>{v.text}</Status>
                      </td>
                      <td data-label="State">
                        <EntryState
                          entry={e}
                          mayReopen={data?.may_reopen ?? false}
                          onChanged={reload}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function EntryState({
  entry,
  mayReopen,
  onChanged,
}: {
  entry: Entry;
  mayReopen: boolean;
  onChanged: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  const reopen = useMutation(async () =>
    api(`/v1/time/${entry.id}/reopen`, { method: 'POST', body: { reason: reason.trim() } }),
  );

  if (entry.status === 'open') return <Status tone="warning">Still clocked in</Status>;
  if (entry.status === 'recorded') return <Status tone="neutral">Awaiting approval</Status>;

  return (
    <>
      <Status tone="good">Approved</Status>
      {mayReopen &&
        (asking ? (
          <div>
            <ErrorBanner error={reopen.error} />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why it is being reopened"
            />
            <div className="hint">This is recorded against your name.</div>
            <div className="actions">
              <button
                disabled={reopen.busy || reason.trim().length < 3}
                onClick={async () => {
                  const r = await reopen.run();
                  if (r) {
                    setAsking(false);
                    setReason('');
                    onChanged();
                  }
                }}
              >
                {reopen.busy ? 'Reopening…' : 'Reopen'}
              </button>
              <button className="secondary" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="secondary small" onClick={() => setAsking(true)}>
            Reopen
          </button>
        ))}
    </>
  );
}
